import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Checkbox,
  Select, MenuItem, Chip, IconButton, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  TextField,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import mammoth from 'mammoth';
import {
  collection, getDocs, query, orderBy, updateDoc, doc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { POST_TYPES } from './LinkedInVerkstad';
import { authFetch } from '../utils/authFetch';

const THEME_COLORS = [
  '#FF6B00','#1565C0','#2E7D32','#6A1B9A',
  '#C62828','#00838F','#E65100','#37474F',
  '#FF8F00','#00695C','#AD1457','#283593',
];

async function extractPdfText(ab) {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(ab) }).promise;
  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(it => it.str).join(' ') + '\n';
  }
  return text;
}

function flattenCampaignThemes(campaigns) {
  const out = [];
  for (const c of campaigns) {
    for (const t of c.themes || []) {
      out.push({ id: t.id, name: t.name, color: t.color || '#888', campaignId: c.id, campaignName: c.name });
    }
  }
  return out;
}

function findMatchingTheme(tema = '', allThemes) {
  const needle = tema.trim().toLowerCase();
  return allThemes.find(t => t.name.trim().toLowerCase() === needle) || null;
}

function findMatchingProduct(productName = '', products) {
  if (!productName?.trim()) return null;
  const needle = productName.trim().toLowerCase();
  return products.find(p => p.name.trim().toLowerCase() === needle)
    || products.find(p => p.name.trim().toLowerCase().includes(needle) || needle.includes(p.name.trim().toLowerCase()))
    || null;
}

function pickColor(index, usedColors = []) {
  for (let i = index; i < THEME_COLORS.length + index; i++) {
    const c = THEME_COLORS[i % THEME_COLORS.length];
    if (!usedColors.includes(c)) return c;
  }
  return THEME_COLORS[index % THEME_COLORS.length];
}

// existingPosts — laddade posts från Firestore (för deduplicering på rubrik)
export default function LinkedInImportDialog({ open, onClose, onImport, existingPosts = [] }) {
  const fileRef       = useRef(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [parsed,      setParsed]      = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [typeMap,     setTypeMap]     = useState({});
  const [themeMap,    setThemeMap]    = useState({}); // i → campaignTheme obj | null
  const [productMap,  setProductMap]  = useState({}); // i → product obj | null
  const [globalType,  setGlobalType]  = useState('tikab');
  const [importing,   setImporting]   = useState(false);
  const [fileName,    setFileName]    = useState('');
  const [campaigns,   setCampaigns]   = useState([]);
  const [products,    setProducts]    = useState([]);
  // Kampanj att skapa NYA teman i
  const [targetCampaignId, setTargetCampaignId] = useState('');
  const [context, setContext] = useState(''); // Manuell kontext till AI

  useEffect(() => {
    if (!open) return;
    Promise.all([
      getDocs(query(collection(db, 'linkedinCampaigns'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'products'), orderBy('name'))),
    ]).then(([campSnap, prodSnap]) => {
      setCampaigns(campSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProducts(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(() => {});
  }, [open]);

  const allThemes = useMemo(() => flattenCampaignThemes(campaigns), [campaigns]);

  const existingByTitle = useMemo(() => {
    const m = {};
    for (const p of existingPosts) {
      if (p.title) m[p.title.trim().toLowerCase()] = p;
    }
    return m;
  }, [existingPosts]);

  function matchExisting(rubrik = '') {
    return existingByTitle[rubrik.trim().toLowerCase()] || null;
  }

  function autoMatchAll(posts) {
    const themeM = {}, productM = {};
    posts.forEach((p, i) => {
      themeM[i]   = findMatchingTheme(p.tema, allThemes);
      productM[i] = findMatchingProduct(p.produkt, products);
    });
    return { themeM, productM };
  }

  function reset() {
    setLoading(false); setError(''); setParsed([]); setSelected(new Set());
    setTypeMap({}); setThemeMap({}); setProductMap({});
    setFileName(''); setImporting(false); setTargetCampaignId('');
  }

  function handleClose() { reset(); onClose(); }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    fileRef.current.value = '';
    setFileName(file.name);
    setError(''); setParsed([]); setSelected(new Set());
    setTypeMap({}); setThemeMap({}); setProductMap({});
    setLoading(true);
    try {
      const ab  = await file.arrayBuffer();
      let text  = '';
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        text = await extractPdfText(ab);
      } else {
        const result = await mammoth.extractRawText({ arrayBuffer: ab });
        text = result.value;
      }
      if (!text.trim()) { setError('Filen verkar tom eller innehåller ingen läsbar text.'); return; }

      const res  = await authFetch('/.netlify/functions/linkedin-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, context: context.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || `HTTP ${res.status}`); return; }

      const posts = data.posts;
      setParsed(posts);
      setSelected(new Set(posts.map((_, i) => i)));
      const { themeM, productM } = autoMatchAll(posts);
      setThemeMap(themeM);
      setProductMap(productM);
      // Auto-sätt type=produkt om AI identifierat produkt
      const tm = {};
      posts.forEach((p, i) => { if (p.typ === 'produkt') tm[i] = 'produkt'; });
      setTypeMap(tm);
    } catch (err) {
      setError(`Kunde inte skicka filen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  function toggleAll()  { setSelected(s => s.size === parsed.length ? new Set() : new Set(parsed.map((_, i) => i))); }
  function toggleOne(i) { setSelected(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; }); }
  function setType(i, t) { setTypeMap(m => ({ ...m, [i]: t })); }
  function setTheme(i, themeId) {
    const theme = themeId ? allThemes.find(t => t.id === themeId) : null;
    setThemeMap(m => ({ ...m, [i]: theme || null }));
  }
  function setProduct(i, prod) { setProductMap(m => ({ ...m, [i]: prod })); }

  // Unika teman bland valda rader som INTE finns i någon kampanj än
  const newThemesNeeded = useMemo(() => {
    const seen = new Set();
    const result = [];
    parsed.forEach((p, i) => {
      if (!selected.has(i) || themeMap[i]) return; // redan matchat
      const tema = p.tema?.trim();
      if (!tema || seen.has(tema)) return;
      seen.add(tema);
      result.push(tema);
    });
    return result;
  }, [parsed, selected, themeMap]);

  async function handleImport() {
    if (!parsed.length) return;
    setImporting(true);
    try {
      // 1. Skapa nya teman i vald kampanj (om vald)
      let updatedAllThemes = [...allThemes];
      if (targetCampaignId && newThemesNeeded.length > 0) {
        const camp = campaigns.find(c => c.id === targetCampaignId);
        if (camp) {
          const usedColors = (camp.themes || []).map(t => t.color);
          const newThemeObjs = newThemesNeeded.map((name, idx) => ({
            id: Date.now().toString() + idx,
            name,
            color: pickColor((camp.themes?.length || 0) + idx, usedColors),
            occurrences: 4, intervalType: 'weeks', intervalValue: 1,
            startDate: '', assignedToUid: '', assignedToName: '',
            productId: '', productName: '', reminders: [],
          }));
          const updatedThemes = [...(camp.themes || []), ...newThemeObjs];
          await updateDoc(doc(db, 'linkedinCampaigns', targetCampaignId), { themes: updatedThemes });
          // Uppdatera lokal state
          setCampaigns(prev => prev.map(c =>
            c.id === targetCampaignId ? { ...c, themes: updatedThemes } : c
          ));
          newThemeObjs.forEach(t => {
            updatedAllThemes.push({ ...t, campaignId: targetCampaignId, campaignName: camp.name });
          });
          // Auto-tilldela de nya temana till rader som saknar matchning
          const newThemeMap = { ...themeMap };
          parsed.forEach((p, i) => {
            if (!selected.has(i) || newThemeMap[i]) return;
            const tema = p.tema?.trim();
            const matched = updatedAllThemes.find(t => t.name === tema);
            if (matched) newThemeMap[i] = matched;
          });
          setThemeMap(newThemeMap);
          // Använd nyuppdaterade themakarta för import
          await doImport(parsed, selected, typeMap, newThemeMap, productMap);
          return;
        }
      }
      await doImport(parsed, selected, typeMap, themeMap, productMap);
    } finally {
      setImporting(false);
    }
  }

  async function doImport(allParsed, sel, tMap, theMap, prodMap) {
    const toImport = allParsed
      .map((p, i) => ({ ...p, i }))
      .filter(p => sel.has(p.i))
      .map(p => {
        const existing = matchExisting(p.rubrik);
        const ct  = theMap[p.i] || null;
        const prod = prodMap[p.i] || null;
        const type = tMap[p.i] || (p.typ === 'produkt' ? 'produkt' : globalType);
        return {
          tema:        p.tema,
          rubrik:      p.rubrik,
          innehall:    p.innehall,
          type,
          existingId:  existing?.id || null,
          themeId:     ct?.id || null,
          themeName:   ct?.name || null,
          themeColor:  ct?.color || null,
          campaignId:  ct?.campaignId || null,
          themeLabel:  ct ? '' : (p.tema || ''),
          productId:   prod?.id || null,
          productName: prod?.name || (p.produkt || ''),
        };
      });
    if (!toImport.length) return;
    await onImport(toImport);
    handleClose();
  }

  const allSelected  = parsed.length > 0 && selected.size === parsed.length;
  const noneSelected = selected.size === 0;
  const newCount     = parsed.filter((p, i) => selected.has(i) && !matchExisting(p.rubrik)).length;
  const updateCount  = parsed.filter((p, i) => selected.has(i) &&  matchExisting(p.rubrik)).length;
  const matchedThemes = parsed.filter((p, i) => selected.has(i) && themeMap[i]).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <UploadFileIcon sx={{ color: '#FF6B00' }} />
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700}>Importera inläggsförslag</Typography>
          <Typography variant="caption" color="text.secondary">Word (.docx) eller PDF</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Kontext */}
        <TextField
          label="Kontext till AI (valfritt)"
          placeholder='T.ex. "Alla idéer i filen gäller produkten FLATBOX" eller "Detta är kampanjidéer för hösten 2025"'
          value={context}
          onChange={e => setContext(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        {/* Upload-yta */}
        <Box
          onClick={() => fileRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: parsed.length ? 'success.light' : 'divider',
            borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
            bgcolor: parsed.length ? 'rgba(76,175,80,0.04)' : 'action.hover',
            transition: 'all 0.2s',
            '&:hover': { borderColor: '#FF6B00', bgcolor: 'rgba(255,107,0,0.04)' },
            mb: 2.5,
          }}
        >
          <input ref={fileRef} type="file"
            accept=".docx,.doc,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: 'none' }} onChange={handleFile} />
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={32} sx={{ color: '#FF6B00' }} />
              <Typography variant="body2" color="text.secondary">Läser och analyserar fil…</Typography>
            </Box>
          ) : parsed.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography fontWeight={700} color="success.main">{parsed.length} förslag hittade i {fileName}</Typography>
              <Typography variant="caption" color="text.secondary">Klicka för att ladda upp ny fil</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <UploadFileIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography fontWeight={600}>Klicka för att välja fil</Typography>
              <Typography variant="caption" color="text.secondary">Word (.docx) eller PDF — max ~1 MB</Typography>
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {parsed.length > 0 && (
          <>
            {/* Kontrollrad */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title={allSelected ? 'Avmarkera alla' : 'Markera alla'}>
                <IconButton size="small" onClick={toggleAll}>
                  {allSelected ? <CheckBoxIcon sx={{ color: '#FF6B00' }} /> : <CheckBoxOutlineBlankIcon />}
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {selected.size} av {parsed.length} valda
                {selected.size > 0 && <>
                  {newCount > 0 && <> &nbsp;·&nbsp;<span style={{ color: '#2E7D32' }}>{newCount} nya</span></>}
                  {updateCount > 0 && <> &nbsp;·&nbsp;<span style={{ color: '#1565C0' }}>{updateCount} uppdateras</span></>}
                  {matchedThemes > 0 && <> &nbsp;·&nbsp;<span style={{ color: '#6A1B9A' }}>{matchedThemes} tema-matchade</span></>}
                </>}
              </Typography>
              <Typography variant="caption" color="text.secondary">Standardtyp:</Typography>
              <Select size="small" value={globalType} onChange={e => setGlobalType(e.target.value)}
                sx={{ minWidth: 110, fontSize: '0.82rem' }}>
                {Object.entries(POST_TYPES).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* Skapa teman i kampanj */}
            {newThemesNeeded.length > 0 && (
              <Alert severity="info" icon={<AutoFixHighIcon />} sx={{ mb: 2, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    {newThemesNeeded.length} nya teman hittade
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {newThemesNeeded.map(t => (
                      <Chip key={t} label={t} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Skapa i kampanj:</Typography>
                    <Select size="small" value={targetCampaignId}
                      onChange={e => setTargetCampaignId(e.target.value)}
                      displayEmpty sx={{ minWidth: 200, fontSize: '0.8rem' }}>
                      <MenuItem value=""><em>Skapa inte — använd fritext</em></MenuItem>
                      {campaigns.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Alert>
            )}

            <Table size="small" sx={{ '& td, & th': { py: 0.75 } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell><Typography variant="caption" fontWeight={700} color="text.secondary">STATUS</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontWeight={700} color="text.secondary">RUBRIK</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontWeight={700} color="text.secondary">PRODUKT</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontWeight={700} color="text.secondary">KAMPANJTEMA</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontWeight={700} color="text.secondary">TYP</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsed.map((p, i) => {
                  const existing  = matchExisting(p.rubrik);
                  const isUpdate  = !!existing;
                  const type      = typeMap[i] || (p.typ === 'produkt' ? 'produkt' : globalType);
                  const typeInfo  = POST_TYPES[type];
                  const ct        = themeMap[i] || null;
                  const prod      = productMap[i] || null;
                  const isNewTheme = !ct && p.tema?.trim() && newThemesNeeded.includes(p.tema.trim());

                  return (
                    <TableRow key={i} hover selected={selected.has(i)}
                      onClick={() => toggleOne(i)}
                      sx={{ cursor: 'pointer', opacity: selected.has(i) ? 1 : 0.45 }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(i)}
                          onChange={() => toggleOne(i)} onClick={e => e.stopPropagation()}
                          sx={{ '&.Mui-checked': { color: '#FF6B00' } }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={isUpdate ? 'Uppdatera' : 'Nytt'} size="small"
                          sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700,
                            bgcolor: isUpdate ? '#E3F2FD' : '#E8F5E9',
                            color:   isUpdate ? '#1565C0' : '#2E7D32' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                          {p.rubrik || '—'}
                        </Typography>
                        {p.tema && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {p.tema}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()} sx={{ minWidth: 150 }}>
                        {p.typ === 'produkt' || type === 'produkt' ? (
                          <Select size="small" value={prod?.id || ''}
                            onChange={e => {
                              const found = products.find(pr => pr.id === e.target.value);
                              setProduct(i, found || null);
                            }}
                            displayEmpty sx={{ fontSize: '0.75rem', minWidth: 140 }}>
                            <MenuItem value=""><em style={{ color: '#999' }}>{p.produkt || '—'}</em></MenuItem>
                            {products.map(pr => (
                              <MenuItem key={pr.id} value={pr.id} sx={{ fontSize: '0.82rem' }}>{pr.name}</MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Tjänst</Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()} sx={{ minWidth: 160 }}>
                        <Select size="small" value={ct?.id || ''}
                          onChange={e => setTheme(i, e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.75rem', minWidth: 150,
                            ...(ct ? { color: ct.color, '& .MuiOutlinedInput-notchedOutline': { borderColor: ct.color + '66' } } : {}) }}>
                          <MenuItem value="">
                            <em style={{ color: isNewTheme ? '#FF8F00' : '#999', fontSize: '0.8rem' }}>
                              {isNewTheme && targetCampaignId ? `✦ Skapas: ${p.tema}` : `Fritext: ${p.tema || '—'}`}
                            </em>
                          </MenuItem>
                          {allThemes.map(t => (
                            <MenuItem key={t.id} value={t.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>{t.name}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{t.campaignName}</Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Select size="small" value={type} onChange={e => setType(i, e.target.value)}
                          sx={{ fontSize: '0.75rem', minWidth: 90, color: typeInfo?.color,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: (typeInfo?.color || '') + '55' } }}>
                          {Object.entries(POST_TYPES).map(([k, v]) => (
                            <MenuItem key={k} value={k} sx={{ fontSize: '0.82rem' }}>{v.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Avbryt</Button>
        {parsed.length > 0 && (
          <Button variant="contained" disabled={noneSelected || importing}
            onClick={handleImport}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700 }}>
            {importing
              ? 'Importerar…'
              : `Importera ${selected.size} inlägg${updateCount > 0 ? ` (${updateCount} uppdateras)` : ''}${targetCampaignId && newThemesNeeded.length ? ` + ${newThemesNeeded.length} teman` : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
